import { H4 } from 'assets/styled';
import { Formik, Field } from 'formik';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApplyWrapper,
  ColumnContainer,
  Wrapper,
  Label,
  RowSpaceBetween,
  Title,
  TitleContainer,
  Container,
  ColumnContainerSelect,
  AdditionalCartDetailsSection,
  AdditionalCartDetailsRow,
  AdditionalCartDetailsInfo,
} from './EditSupplierSettingsModal.styled';
import { Input } from 'components/Input/InputFormik';
import Button from 'components/Button/Button';
import { editSupplierSettingsSchema } from './validation';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { FormikOuter } from '../EditCompanyDataModal/EditCompanyDataModal.styled';
import { useEditCompany } from '../hooks';
import { IEditCompanyDataDTO } from '../types';
import { useGetCurrencyOptions } from '../helpers';
import FormikSelect from 'components/Select/FormikSelect';
import { Modal } from 'components/Modals/Modal/Modal';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { grossPriceVisibilityOptions } from './constants';
import { useGetCountryOptions } from 'utils/hooks/useGetCountryOptions';
import { AdditionalCartDetailsModal } from '../AdditionalCartDetailsModal/AdditionalCartDetailsModal';
import { SUPPORTED_LANGUAGES } from '../AdditionalCartDetailsModal/constants';
import { IAdditionalCartDetails } from 'types/Company.types';

interface IEditSupplierSettingsModalProps {
  onCancel: () => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  data: any;
}
export const EditSupplierSettingsModal = ({
  isOpen,
  setIsOpen,
  onCancel,
  data,
}: IEditSupplierSettingsModalProps) => {
  const { t } = useTranslation();
  const { isSmallTablet, isTablet } = useBreakpointFlag();
  const currencyOptions = useGetCurrencyOptions();
  const countryOptions = useGetCountryOptions();

  const { company } = useSelector((state: IRootReducerState) => state.userInfo);

  const { mutate: editCompany, isLoading } = useEditCompany(Number(company.id));

  const [
    isAdditionalCartDetailsModalOpen,
    setIsAdditionalCartDetailsModalOpen,
  ] = useState<boolean>(false);

  const handleEditCompanySettings = (values: any) => {
    const editCompanyDataDTO: IEditCompanyDataDTO = {
      name: data.name,
      vat_number: data.vat_number,
      coc_number: data.coc_number,
      website: data.website,
      email: data.email,
      phone_number: data.phone_number,
      address: data.address,
      company_settings: {
        vat_percentage: values.vat_percentage,
        pricing_currency: values.pricing_currency,
        display_currency: data.company_settings?.display_currency,
        quotation_prefix: data.company_settings?.quotation_prefix,
        order_prefix: values.order_prefix,
        is_gross_visible: values.is_gross_visible,
        allowed_delivery_countries: values.allowed_delivery_countries,
        fixed_exchange_rates: data.company_settings?.fixed_exchange_rates,
        additional_cart_details: values.additional_cart_details ?? {},
      },
    };
    editCompany(editCompanyDataDTO, {
      onSuccess: () => setIsOpen(false),
    });
  };

  const getInitialValues = () => {
    return {
      vat_percentage: data?.company_settings?.vat_percentage as number,
      pricing_currency: data?.company_settings?.pricing_currency as string,
      order_prefix: data?.company_settings?.order_prefix as string,
      is_gross_visible: data?.company_settings?.is_gross_visible as boolean,
      allowed_delivery_countries:
        (data?.company_settings?.allowed_delivery_countries as string[]) ?? [],
      additional_cart_details:
        (data?.company_settings
          ?.additional_cart_details as IAdditionalCartDetails | null) ?? null,
    };
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      modalStyle={{
        position: 'fixed',
        margin: 'auto',
        width: isTablet ? '90%' : '700rem',
        minWidth: isTablet ? '90%' : 'auto',
      }}
    >
      <Container>
        <TitleContainer>
          <Title>{t('Supplier settings')}</Title>
        </TitleContainer>
        <FormikOuter>
          <Formik
            enableReinitialize
            onSubmit={() => {
              //
            }}
            initialValues={getInitialValues()}
            validationSchema={editSupplierSettingsSchema}
          >
            {({ values, setFieldValue, handleBlur, errors, isValid }) => {
              const handleBlurFunction = (e: any, name: string) => {
                const value = e.target.value.trim();
                setFieldValue(name, value);
                handleBlur(e);
              };
              return (
                <Wrapper>
                  <RowSpaceBetween>
                    <ColumnContainer>
                      <Label>
                        <H4>{t('Vat percentage')}</H4>
                      </Label>
                      <Input
                        errorMessage={
                          errors['vat_percentage']
                            ? errors['vat_percentage']
                            : ''
                        }
                        onBlur={(e) => {
                          handleBlurFunction(e, 'vat_percentage');
                        }}
                        height={'43rem'}
                        name="vat_percentage"
                        wrapperStyles={{
                          width: isSmallTablet ? '100%' : '300rem',
                        }}
                      />
                    </ColumnContainer>
                    <ColumnContainerSelect>
                      <Label>
                        <H4>{t('Currency')}</H4>
                      </Label>
                      <Field
                        name="pricing_currency"
                        component={FormikSelect}
                        options={currencyOptions}
                        isMulti={false}
                        errorMessage={
                          errors['pricing_currency']
                            ? errors['pricing_currency']
                            : ''
                        }
                        disabled={false}
                        width="100%"
                      />
                    </ColumnContainerSelect>
                  </RowSpaceBetween>
                  <RowSpaceBetween>
                    <ColumnContainer>
                      <Label>
                        <H4>{t('Logyx Order ID prefix')}</H4>
                      </Label>
                      <Input
                        errorMessage={
                          errors['order_prefix'] ? errors['order_prefix'] : ''
                        }
                        onBlur={(e) => {
                          handleBlurFunction(e, 'order_prefix');
                        }}
                        height={'43rem'}
                        name="order_prefix"
                        wrapperStyles={{
                          width: isSmallTablet ? '100%' : '300rem',
                        }}
                      />
                    </ColumnContainer>
                    <ColumnContainerSelect>
                      <Label>
                        <H4>{t('Gross price visibility')}</H4>
                      </Label>
                      <Field
                        name="is_gross_visible"
                        component={FormikSelect}
                        options={grossPriceVisibilityOptions}
                        isMulti={false}
                        errorMessage={
                          errors['is_gross_visible']
                            ? errors['is_gross_visible']
                            : ''
                        }
                        disabled={false}
                        width="100%"
                      />
                    </ColumnContainerSelect>
                  </RowSpaceBetween>
                  <RowSpaceBetween>
                    <ColumnContainerSelect>
                      <Label>
                        <H4>{t('Allowed delivery countries')}</H4>
                      </Label>
                      <Field
                        name="allowed_delivery_countries"
                        component={FormikSelect}
                        options={countryOptions}
                        isMulti={true}
                        errorMessage={
                          errors['allowed_delivery_countries']
                            ? errors['allowed_delivery_countries']
                            : ''
                        }
                        disabled={false}
                        width="100%"
                      />
                    </ColumnContainerSelect>
                    <AdditionalCartDetailsSection>
                      <Label>
                        <H4>{t('Additional cart details')}</H4>
                      </Label>
                      <AdditionalCartDetailsRow>
                        <AdditionalCartDetailsInfo>
                          {values.additional_cart_details
                            ? `${
                                Object.keys(values.additional_cart_details)
                                  .length
                              }/${SUPPORTED_LANGUAGES.length} ${t(
                                'configured'
                              )}`
                            : '-'}
                        </AdditionalCartDetailsInfo>
                        <Button
                          onClick={() =>
                            setIsAdditionalCartDetailsModalOpen(true)
                          }
                          secondary
                          width="100rem"
                          label={t('Configure')}
                        />
                      </AdditionalCartDetailsRow>
                    </AdditionalCartDetailsSection>
                  </RowSpaceBetween>
                  <ApplyWrapper>
                    <Button
                      onClick={onCancel}
                      secondary
                      width="200rem"
                      label={t('Cancel')}
                    />
                    <Button
                      disabled={!isValid || isLoading}
                      onClick={() => {
                        if (isValid) {
                          handleEditCompanySettings(values);
                        }
                      }}
                      primary
                      width="200rem"
                      label={t('Apply')}
                    />
                  </ApplyWrapper>
                  {isAdditionalCartDetailsModalOpen && (
                    <AdditionalCartDetailsModal
                      isOpen={isAdditionalCartDetailsModalOpen}
                      setIsOpen={setIsAdditionalCartDetailsModalOpen}
                      onCancel={() =>
                        setIsAdditionalCartDetailsModalOpen(false)
                      }
                      onSave={(details) =>
                        setFieldValue('additional_cart_details', details)
                      }
                      additionalCartDetails={values.additional_cart_details}
                    />
                  )}
                </Wrapper>
              );
            }}
          </Formik>
        </FormikOuter>
      </Container>
    </Modal>
  );
};
